import { useMemo } from 'react'

import { DateTime } from 'luxon'

import {
  matchFilterArrayIncludes, matchFilterString, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { UserModel } from '@models/user'
import type { EntityOptions } from '@redux/modules/module'

const watchEntityKeys = ['users']

export type UseUsersFilters = {
  actable_types: string[],
  agency_id?: number,
  api_key_token?: string,
  brand_id?: number,
  brand_user?: boolean,
  created_after?: string,
  provider_id?: number,
  role_id?: number,
  string?: string,
  user_ids?: number[],
}

type UseUsersOptions = {
  entityKey?: string,
  filters?: UseUsersFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useUsers(options: UseUsersOptions = {}) {
  const { entityKey, filters, requestOptions } = options

  const {
    actable_types: filterActableType,
    agency_id: filterAgencyId,
    api_key_token: filterToken,
    brand_id: filterBrandId,
    brand_user: filterBrandUser,
    created_after: filterCreatedAfter,
    provider_id: filterProviderId,
    role_id: filterRoleId,
    string,
    user_ids: filterUserIds,
  } = filters || {}

  const { updatedEntities: { users: usersUpdatedAt } } = useWatchEntityUpdates(watchEntityKeys)

  const apiKeys = useSelector(reduxState => reduxState.entities.apiKeys)
  const users = useSelector(reduxState => reduxState.entities.users)

  const filteredUsers = useMemo(() => {
    const filtered = Object.values(users).filter((user: UserModel) => {
      const {
        actable_type,
        api_keys = [],
        association_id,
        association_type,
        brand_id,
        brand_user,
        // @ts-expect-error TS2339
        created_at,
        email,
        full_name,
        id,
        role_id,
        username,
      } = user

      const userApiTokens = filterToken ? api_keys.map(apiKeyId => apiKeys[apiKeyId].token) : []
      const agencyId = association_type === 'Agency' ? association_id : null
      const providerId = association_type === 'Provider' ? association_id : null

      const matchActableType = filterActableType ? filterActableType.includes(actable_type) : true
      const matchApiKey = matchFilterArrayIncludes(userApiTokens, filterToken)
      const matchAgencyId = matchFilterNumber(agencyId, filterAgencyId)
      const matchBrandId = matchFilterNumber(brand_id, filterBrandId)
      const matchBrandUser = filterBrandUser !== undefined ? filterBrandUser === brand_user : true
      const matchEmail = matchFilterString(email, string)
      const matchFullName = matchFilterString(full_name, string)
      const matchId = id.toString() === string
      const matchProviderId = matchFilterNumber(providerId, filterProviderId)
      const matchRoleId = matchFilterNumber(role_id, filterRoleId)
      const matchUsername = matchFilterString(username, string)
      const matchFromUserIds = filterUserIds ? matchFilterArrayIncludes(filterUserIds, id) : true

      const createdAtDT = created_at ? DateTime.fromISO(created_at, { zone: 'utc' }) : undefined
      const matchIsCreatedAfter = filterCreatedAfter && createdAtDT
        // @ts-expect-error TS2365
        ? createdAtDT >= filterCreatedAfter
        : true

      const matchString = matchId || matchFullName || matchEmail || matchUsername

      return matchActableType
        && matchApiKey
        && matchAgencyId
        && matchBrandId
        && matchBrandUser
        && matchIsCreatedAfter
        && matchFromUserIds
        && matchProviderId
        && matchRoleId
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'full_name')
  }, [usersUpdatedAt, JSON.stringify(filters)])

  const hasFilteredUsers = !!filteredUsers.length
  const filteredUsersCount = filteredUsers.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredUsersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingUsers } = useReduxAction(
    'users',
    'loadUsers',
    {
      entityKey,
      ...requestOptions,
      ...filtersWithOffset,
      include_user_api_keys: !!filterToken,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    { shouldPerformFn: () => performHttpRequests },
  )

  const officeAdminUsers = useMemo(() => {
    const filtered = filteredUsers.filter(user => !user.brand_user && user.actable_type === 'OfficeAdministrator')
    return filtered
  }, [usersUpdatedAt, filterAgencyId, filteredUsers.length])

  const agentUsers = useMemo(() => {
    const filtered = filteredUsers.filter(user => user.actable_type === 'Agent')
    return filtered
  }, [usersUpdatedAt, filterAgencyId, filteredUsers.length])

  return {
    agentUsers,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredUsers,
    filteredUsersCount,
    hasFilteredUsers,
    loading: loadingUsers,
    officeAdminUsers,
  }
}

export default useUsers
